import { z } from "zod";
import { recurringScheduleSchema } from "../scheduling/recurringSchedule.js";
import {
  AutomationDeliveryProvider,
  automationDeliverySchema,
  automationIdSchema,
  automationPlaybooksScopeSchema,
  automationResultModeSchema,
  automationSubscriberRecipientsSchema,
  automationTemplateIdSchema,
  checkIsSharedReportConfig,
  checkIsTeammateCoachingSettings,
  getCoachingRecipients,
  updateAutomationDeliveryEditorValue,
  updateAutomationPeopleSelection,
  updateAutomationPlaybookIds,
  updateAutomationRecipientSelection,
  updateAutomationResultMode,
  updateAutomationSubscribersEditorValue,
  updateCoachingRecipients
} from "./automation.js";
import { automationRecipientSelectionSchema } from "./automationReportRouting.js";
import {
  automationProposalKindSchema,
  copilotProposalIdSchema,
  copilotProposalSummarySchema
} from "./copilotProposal.js";
import { groupIdSchema } from "./group.js";
import { playbookIdSchema } from "./playbook.js";
import { teammateIdSchema } from "./teammate.js";
const automationPeopleProposalSchema = z.object({
  includes_all_teammates: z.boolean(),
  includes_all_managers: z.boolean(),
  teammate_ids: z.array(teammateIdSchema),
  group_ids: z.array(groupIdSchema).optional()
});
function readPeopleProposal(people) {
  return {
    includesAllTeammates: people.includes_all_teammates,
    includesAllManagers: people.includes_all_managers,
    teammateIds: people.teammate_ids,
    groupIds: people.group_ids ?? []
  };
}
const instructionsProposalSchema = z.string().optional();
const reportConfigurationFieldsSchema = z.object({
  template_family: z.literal("report"),
  name: z.string().min(1).optional(),
  instructions: instructionsProposalSchema,
  playbook_ids: automationPlaybooksScopeSchema.optional(),
  trigger: recurringScheduleSchema.optional(),
  result_mode: automationResultModeSchema.optional().describe(
    'Whose result each recipient sees: "shared" is one result everyone receives alike, "own" gives each selected person a result about themselves, "custom" leaves it to the instructions. Decides which of recipients, people and subscribers apply.'
  ),
  recipients: automationRecipientSelectionSchema.optional().describe(
    "The teammates and groups a shared result goes to. Not used when delivery posts to channels. For own results, pick the people in `people` instead."
  ),
  people: automationPeopleProposalSchema.optional().describe(
    'Who each get their own result, when result_mode is "own": the all-teammates and all-managers options plus hand-picked teammates and groups. Refused on a shared result.'
  ),
  subscribers: automationSubscriberRecipientsSchema.optional().describe(
    'Teammates copied on every own result, when result_mode is "own" and the results go to people. Refused on a shared or channel-posted result; leave it out rather than sending an empty list.'
  )
});
const deliveryDescription = "How results are delivered: email (the default), or Slack or Teams as a direct message to each recipient or a post to channels from list-delivery-channels.";
const reportConfigurationProposalSchema = reportConfigurationFieldsSchema.extend({
  delivery: automationDeliverySchema.optional().describe(deliveryDescription)
});
const coachingConfigurationInputSchema = z.object({
  template_family: z.literal("coaching"),
  name: z.string().min(1).optional(),
  instructions: instructionsProposalSchema,
  playbook_id: playbookIdSchema.optional(),
  recipients: automationPeopleProposalSchema.optional()
});
const coachingConfigurationProposalSchema = coachingConfigurationInputSchema.extend({
  should_notify_teammates: z.boolean().optional(),
  should_notify_managers: z.boolean().optional(),
  email_recipients: automationPeopleProposalSchema.optional()
});
const automationConfigurationProposalSchema = z.discriminatedUnion(
  "template_family",
  [reportConfigurationProposalSchema, coachingConfigurationProposalSchema]
);
const chatDeliveryModeInputSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("direct_message") }),
  z.object({
    kind: z.literal("channel"),
    channels: z.array(
      z.object({
        id: z.string().describe("A channel id list-delivery-channels returned.")
      })
    )
  })
]);
const automationDeliveryInputSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal(AutomationDeliveryProvider.EMAIL) }),
  z.object({
    provider: z.literal(AutomationDeliveryProvider.SLACK),
    destination: chatDeliveryModeInputSchema
  }),
  z.object({
    provider: z.literal(AutomationDeliveryProvider.TEAMS),
    destination: chatDeliveryModeInputSchema
  })
]);
const reportConfigurationInputSchema = reportConfigurationFieldsSchema.extend({
  delivery: automationDeliveryInputSchema.optional().describe(deliveryDescription)
});
const automationConfigurationInputSchema = z.discriminatedUnion("template_family", [
  reportConfigurationInputSchema,
  coachingConfigurationInputSchema
]).refine(
  (configuration) => Object.keys(configuration).some((field) => field !== "template_family"),
  { message: "A proposal must change at least one field" }
);
const newAutomationConfigurationInputSchema = z.discriminatedUnion(
  "template_family",
  [
    reportConfigurationInputSchema.extend({
      playbook_ids: z.tuple([playbookIdSchema]).rest(playbookIdSchema)
    }),
    coachingConfigurationInputSchema.extend({
      playbook_id: playbookIdSchema
    })
  ]
);
function applyAutomationConfigurationProposal(current, configuration, context) {
  const name = configuration.name ?? current.name;
  const { settings } = current;
  if (configuration.template_family === "coaching") {
    if (!checkIsTeammateCoachingSettings(settings)) {
      throw new Error(
        "A coaching configuration proposal cannot configure a report automation"
      );
    }
    const currentPeople = getCoachingRecipients(settings);
    const proposedPeople = configuration.recipients ?? configuration.email_recipients;
    const changesAudiences = configuration.should_notify_teammates !== void 0 && configuration.should_notify_teammates !== currentPeople.includesAllTeammates || configuration.should_notify_managers !== void 0 && configuration.should_notify_managers !== currentPeople.includesAllManagers;
    if (!proposedPeople && changesAudiences && currentPeople.groupIds.length > 0) {
      throw new Error(
        "Specify recipients to change an audience selected through groups"
      );
    }
    const recipientSettings = proposedPeople ? updateCoachingRecipients(settings, readPeopleProposal(proposedPeople)) : changesAudiences ? updateCoachingRecipients(settings, {
      includesAllTeammates: configuration.should_notify_teammates ?? currentPeople.includesAllTeammates,
      includesAllManagers: configuration.should_notify_managers ?? currentPeople.includesAllManagers,
      teammateIds: currentPeople.teammateIds.filter(
        (id) => context.managerTeammateIds.includes(id) ? configuration.should_notify_managers === void 0 : configuration.should_notify_teammates === void 0
      ),
      groupIds: []
    }) : settings;
    return {
      name,
      settings: {
        ...recipientSettings,
        playbook_id: configuration.playbook_id ?? settings.playbook_id,
        coaching_focus: configuration.instructions ?? settings.coaching_focus
      }
    };
  }
  if (checkIsTeammateCoachingSettings(settings)) {
    throw new Error(
      "A report configuration proposal cannot configure a coaching automation"
    );
  }
  let next = settings;
  const issues = [];
  if (configuration.result_mode) {
    next = updateAutomationResultMode(next, configuration.result_mode);
  }
  if (configuration.playbook_ids) {
    next = updateAutomationPlaybookIds(next, configuration.playbook_ids);
  }
  if (configuration.trigger) {
    next = { ...next, trigger: configuration.trigger };
  }
  if (configuration.recipients) {
    next = updateAutomationRecipientSelection(next, configuration.recipients);
  }
  if (configuration.people) {
    const reportConfig = next.report_config;
    const people = readPeopleProposal(configuration.people);
    if (!checkIsSharedReportConfig(reportConfig)) {
      next = updateAutomationPeopleSelection(
        next,
        people,
        context.managerTeammateIds
      );
    } else if (checkPeopleSelectionPicksAnyone(people)) {
      issues.push(
        '`people` only applies to own results (result_mode "own"); a shared result names who receives it in `recipients`.'
      );
    }
  }
  if (configuration.delivery) {
    next = updateAutomationDeliveryEditorValue(next, configuration.delivery);
  }
  if (configuration.subscribers) {
    if (next.report_config.definitions.some(
      (definition) => definition.routing.type === "instance_recipient"
    )) {
      next = updateAutomationSubscribersEditorValue(
        next,
        configuration.subscribers
      );
    } else if (configuration.subscribers.teammate_ids.length > 0) {
      issues.push(
        "`subscribers` only applies to own results that go to people; a shared or channel-posted result copies nobody. Leave it out."
      );
    }
  }
  if (configuration.instructions !== void 0) {
    next = { ...next, instructions: configuration.instructions };
  }
  if (issues.length > 0) {
    throw new Error(issues.join(" "));
  }
  return { name, settings: next };
}
function checkPeopleSelectionPicksAnyone(people) {
  return people.includesAllTeammates || people.includesAllManagers || people.teammateIds.length > 0 || people.groupIds.length > 0;
}
function readAutomationInstructions(settings) {
  return checkIsTeammateCoachingSettings(settings) ? settings.coaching_focus : settings.instructions;
}
const proposeAutomationToolInputSchema = z.object({
  change: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("create_automation"),
      proposal_id: copilotProposalIdSchema.optional().describe(
        "When refining an automation that has not been created yet, the pending create_automation proposal to replace. Submit the whole revised draft. Omit only for a separate new automation."
      ),
      template_id: automationTemplateIdSchema.describe(
        "Which template the automation is built from. It cannot be changed afterwards."
      ),
      name: z.string().min(1),
      configuration: newAutomationConfigurationInputSchema.describe(
        "The automation's configuration, its instructions included. Fields left out keep the template's default."
      ),
      summary: copilotProposalSummarySchema.describe(
        "One plain sentence on what the automation does. Shown on the proposal card."
      )
    }),
    z.object({
      kind: z.literal("update_automation"),
      automation_id: automationIdSchema,
      proposal_id: copilotProposalIdSchema.optional().describe(
        "The pending update_automation proposal on this automation from this conversation to build on: fields you leave out keep its proposed values. Pass your latest proposal's id when refining it; omit to propose changes against the saved automation only."
      ),
      configuration: automationConfigurationInputSchema.describe(
        "Only the fields that change, instructions included. Fields left out keep what the automation has saved."
      ),
      summary: copilotProposalSummarySchema.describe(
        "One plain sentence on what this proposal changes compared with the saved automation, including the changes it keeps from the proposal named by proposal_id. Shown on the proposal card."
      )
    })
  ]).describe(
    "The change to propose: a whole new automation (`create_automation`), or changes to an existing one (`update_automation`). Either builds on the proposal named by `proposal_id`, or starts fresh without one."
  )
});
const proposeAutomationToolOutputSchema = z.object({
  proposal_id: copilotProposalIdSchema,
  automation_id: automationIdSchema,
  kind: automationProposalKindSchema
});
export {
  applyAutomationConfigurationProposal,
  automationConfigurationInputSchema,
  automationConfigurationProposalSchema,
  automationDeliveryInputSchema,
  newAutomationConfigurationInputSchema,
  proposeAutomationToolInputSchema,
  proposeAutomationToolOutputSchema,
  readAutomationInstructions
};
//# sourceMappingURL=automationProposal.js.map
